/**
 * BillingStatusBanner Component
 *
 * Displays global banners for billing status issues:
 * - Payment failed (red)
 * - Past due invoice (red)
 * - Requires authentication (amber)
 * - Cancellation scheduled (amber)
 * - Downgrade scheduled (amber)
 *
 * Banners are dismissible per session (sessionStorage).
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Organization } from "../../lib/api";
import {
  X,
  AlertTriangle,
  CreditCard,
  Clock,
  RefreshCw,
} from "lucide-react";
import { settingsPath } from "../../lib/settings-path";

interface BillingStatusBannerProps {
  organization: Organization;
  isOwner: boolean;
}

type BannerType =
  | "payment_failed"
  | "past_due"
  | "requires_action"
  | "cancellation_scheduled"
  | "downgrade_scheduled";

interface BannerConfig {
  type: BannerType;
  priority: number;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  message: string;
  actionText: string;
  actionUrl: string;
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return "soon";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "soon";
  }
}

function formatTierName(tier: string | undefined): string {
  if (!tier) return "Free";
  return tier.charAt(0).toUpperCase() + tier.slice(1).replace("_", " ");
}

export function BillingStatusBanner({
  organization,
  isOwner,
}: BillingStatusBannerProps) {
  const [dismissedBanners, setDismissedBanners] = useState<Set<BannerType>>(
    new Set()
  );

  // Load dismissed banners from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem("dismissed_billing_banners");
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as BannerType[];
        setDismissedBanners(new Set(parsed));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  // Save dismissed banners to sessionStorage
  const dismissBanner = (type: BannerType) => {
    const newDismissed = new Set(dismissedBanners);
    newDismissed.add(type);
    setDismissedBanners(newDismissed);
    sessionStorage.setItem(
      "dismissed_billing_banners",
      JSON.stringify(Array.from(newDismissed))
    );
  };

  // Determine which banners to show based on organization state
  const banners: BannerConfig[] = [];

  // Payment failed - highest priority
  if (
    organization.billing_status === "past_due" &&
    organization.failed_payment_count &&
    organization.failed_payment_count > 0
  ) {
    banners.push({
      type: "payment_failed",
      priority: 1,
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-700",
      iconColor: "text-red-600",
      icon: CreditCard,
      title: "Payment Failed",
      message:
        "Your payment method was declined. Update your payment method to avoid service interruption.",
      actionText: "Update Payment",
      actionUrl: settingsPath("billing"),
    });
  }

  // Past due (invoice billing, no failed card payment)
  if (
    organization.billing_status === "past_due" &&
    (!organization.failed_payment_count ||
      organization.failed_payment_count === 0) &&
    organization.billing_method === "invoice"
  ) {
    banners.push({
      type: "past_due",
      priority: 2,
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-700",
      iconColor: "text-red-600",
      icon: Clock,
      title: "Invoice Overdue",
      message: "Your invoice is past due. Pay now to avoid late fees and service interruption.",
      actionText: "Pay Now",
      actionUrl: settingsPath("billing"),
    });
  }

  // Requires action (3D Secure / SCA authentication needed)
  if (organization.billing_status === "requires_action") {
    banners.push({
      type: "requires_action",
      priority: 3,
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-800",
      iconColor: "text-amber-500",
      icon: AlertTriangle,
      title: "Payment Requires Authentication",
      message:
        "Your payment needs additional verification. Complete authentication to avoid service interruption.",
      actionText: "Manage Payment",
      actionUrl: settingsPath("billing"),
    });
  }

  // Cancellation scheduled
  if (organization.cancel_at_period_end && organization.cancel_effective_at) {
    banners.push({
      type: "cancellation_scheduled",
      priority: 4,
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-800",
      iconColor: "text-amber-500",
      icon: AlertTriangle,
      title: "Subscription Ending",
      message: `Your subscription will end on ${formatDate(organization.cancel_effective_at)}. You'll be downgraded to the Free tier.`,
      actionText: "Reactivate",
      actionUrl: settingsPath("billing"),
    });
  }

  // Downgrade scheduled
  if (
    organization.downgrade_scheduled_tier &&
    organization.downgrade_effective_at
  ) {
    banners.push({
      type: "downgrade_scheduled",
      priority: 5,
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-800",
      iconColor: "text-amber-500",
      icon: RefreshCw,
      title: "Plan Change Scheduled",
      message: `Your plan will change to ${formatTierName(organization.downgrade_scheduled_tier)} on ${formatDate(organization.downgrade_effective_at)}.`,
      actionText: "Cancel Change",
      actionUrl: settingsPath("billing"),
    });
  }

  // Filter out dismissed banners and sort by priority
  const visibleBanners = banners
    .filter((b) => !dismissedBanners.has(b.type))
    .sort((a, b) => a.priority - b.priority);

  // Only show the highest priority banner
  const activeBanner = visibleBanners[0];

  if (!activeBanner) {
    return null;
  }

  const Icon = activeBanner.icon;

  return (
    <div
      className={`${activeBanner.bgColor} ${activeBanner.borderColor} border-b px-4 py-3`}
    >
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${activeBanner.iconColor} flex-shrink-0`} />
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
            <span className={`font-medium ${activeBanner.textColor}`}>
              {activeBanner.title}:
            </span>
            <span className={`text-sm ${activeBanner.textColor} opacity-90`}>
              {activeBanner.message}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
          {isOwner && (
            <Link
              to={activeBanner.actionUrl}
              className={`text-sm font-medium ${activeBanner.textColor} hover:underline whitespace-nowrap`}
            >
              {activeBanner.actionText}
            </Link>
          )}
          <button
            onClick={() => dismissBanner(activeBanner.type)}
            className={`${activeBanner.textColor} opacity-70 hover:opacity-100 transition-opacity`}
            aria-label="Dismiss banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default BillingStatusBanner;